/**
 * Strips and decodes Intercom-flavored HTML so it renders cleanly in
 * the chat UI. Notes / bot messages occasionally contain HTML markup
 * and placeholder tokens (`[attachment: "name"]`) that aren't useful
 * to merchants.
 */

const INTERCOM_ATTACHMENT_PLACEHOLDER_REGEX =
  /\[(?:attachment|image|video)\s*:?\s*"[^"]*"\]/gi;
const INTERCOM_SIMPLE_MEDIA_PLACEHOLDER_REGEX =
  /\[(?:gif|image|video|attachment)\]/gi;
const INTERCOM_HTML_BREAK_REGEX = /<(?:br\s*\/?|\/p|\/div|\/li)>/gi;
const INTERCOM_HTML_LIST_ITEM_OPEN_REGEX = /<li[^>]*>/gi;
const INTERCOM_HTML_TAG_REGEX = /<[^>]+>/g;

function decodeHtmlEntities(text: string): string {
  return text
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
}

export function sanitizeIntercomBody(body: string | null | undefined): string {
  if (!body) {
    return "";
  }
  const stripped = body
    .replace(INTERCOM_HTML_BREAK_REGEX, "\n")
    .replace(INTERCOM_HTML_LIST_ITEM_OPEN_REGEX, "- ")
    .replace(INTERCOM_HTML_TAG_REGEX, "")
    .replace(/\n{2,}/g, "\n")
    .trim()
    .replace(/\n/g, " ")
    .replace(INTERCOM_ATTACHMENT_PLACEHOLDER_REGEX, "")
    .replace(INTERCOM_SIMPLE_MEDIA_PLACEHOLDER_REGEX, "");
  return decodeHtmlEntities(stripped).replace(/\s+/g, " ").trim();
}

export interface IntercomAttachment {
  url: string;
  name?: string;
  contentType?: string;
}

export type MediaMarkerKind = "image-data" | "video-data" | "document-data";

/**
 * Classifies an attachment for the chat renderer. Images are first-
 * class content parts (so they render inline); videos and documents
 * are emitted as text-content media markers that the chat renderer
 * parses back out at render time.
 */
export function classifyAttachment(attachment: IntercomAttachment): {
  marker: MediaMarkerKind;
  isImage: boolean;
  isVideo: boolean;
} {
  const isImage = attachment.contentType?.startsWith("image/") ?? false;
  const isVideo = attachment.contentType?.startsWith("video/") ?? false;
  return {
    isImage,
    isVideo,
    marker: isImage ? "image-data" : isVideo ? "video-data" : "document-data",
  };
}

export function buildAttachmentMarker(attachment: IntercomAttachment): string {
  const { isImage, isVideo, marker } = classifyAttachment(attachment);
  const data = JSON.stringify({
    url: attachment.url,
    type: attachment.contentType,
    name:
      isImage || isVideo ? attachment.name : attachment.name || "Attachment",
  });
  return `[[[[${marker}:${data}]]]]`;
}
